/**
 * AllergiesSection (Jun 2026) — structured allergies on the Meds & Vitals
 * tab. Single source of truth = /clients/{id}/allergies entries + NKDA
 * flag; the backend keeps the legacy free-text client.allergies in sync
 * as a derived summary (notes auto-populate, intake wizard, PDFs).
 * Self-contained: fetches its own data, used by legacy + V2 via the
 * shared MedsVitalsTab.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Plus, Edit, RotateCcw, Archive, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { useCollapsePref } from '../../hooks/useCollapsePref';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITIES = ['mild', 'moderate', 'severe'];
const sevBadge = (sev) => {
  const s = (sev || '').toLowerCase();
  if (s === 'severe') return 'bg-red-100 text-red-800 border-red-300';
  if (s === 'moderate') return 'bg-orange-100 text-orange-800 border-orange-300';
  if (s === 'mild') return 'bg-yellow-100 text-yellow-800 border-yellow-300';
  return 'bg-gray-100 text-gray-600 border-gray-300';
};

// Standalone add/edit dialog — reused by AllergiesSection, the intake
// wizard, and the note-field quick add (AddAllergyInline).
export const AllergyFormDialog = ({ open, onOpenChange, clientId, editing, onSaved }) => {
  const [form, setForm] = useState({ allergen: '', severity: '', reaction: '', notes: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(editing
        ? { allergen: editing.allergen || '', severity: editing.severity || '', reaction: editing.reaction || '', notes: editing.notes || '' }
        : { allergen: '', severity: '', reaction: '', notes: '' });
    }
  }, [open, editing]);

  const save = async () => {
    if (!form.allergen.trim()) { toast.error('Allergen is required'); return; }
    setBusy(true);
    try {
      let resp;
      if (editing) {
        resp = await axios.put(`${API}/clients/${clientId}/allergies/${editing.id}`, form);
        toast.success('Allergy updated');
      } else {
        resp = await axios.post(`${API}/clients/${clientId}/allergies`, form);
        toast.success('Allergy added');
      }
      onOpenChange(false);
      onSaved?.(resp.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save allergy');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="allergy-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Allergy' : 'Add Allergy'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Allergen *</Label>
            <Input
              value={form.allergen}
              onChange={(e) => setForm({ ...form, allergen: e.target.value })}
              placeholder="e.g., Penicillin, Peanuts, Latex"
              data-testid="allergy-allergen-input"
            />
          </div>
          <div>
            <Label>Severity</Label>
            <div className="flex gap-1.5 mt-1">
              {SEVERITIES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setForm({ ...form, severity: form.severity === s ? '' : s })}
                  className={`text-xs px-2.5 py-1 rounded-full border capitalize ${form.severity === s ? sevBadge(s) + ' font-semibold' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                  data-testid={`allergy-severity-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>Reaction</Label>
            <Input
              value={form.reaction}
              onChange={(e) => setForm({ ...form, reaction: e.target.value })}
              placeholder="e.g., hives, anaphylaxis"
              data-testid="allergy-reaction-input"
            />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              data-testid="allergy-notes-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" onClick={save} disabled={busy} data-testid="allergy-save-btn">
            {busy ? 'Saving…' : editing ? 'Save Changes' : 'Add Allergy'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Self-contained "+ Add Allergy" button + dialog for note fields
// (client_allergies field type). Saves to the chart and reports the new
// entry upward so the note snapshot can include it.
export const AddAllergyInline = ({ clientId, disabled, onSaved }) => {
  const [open, setOpen] = useState(false);
  if (!clientId) return null;
  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="mt-2 border-yellow-400 text-yellow-800 hover:bg-yellow-100"
        onClick={() => setOpen(true)}
        disabled={disabled}
        data-testid="note-add-allergy-btn"
      >
        <Plus className="h-4 w-4 mr-1" /> Add Allergy
      </Button>
      <AllergyFormDialog open={open} onOpenChange={setOpen} clientId={clientId} editing={null} onSaved={onSaved} />
    </>
  );
};

export const AllergiesSection = ({ client }) => {
  const [allergies, setAllergies] = useState([]);
  const [nkda, setNkda] = useState(false);
  const [legacyText, setLegacyText] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  // Collapsible with per-user persisted preference (default open — safety)
  const [sectionOpen, toggleSection] = useCollapsePref('medsvitals-allergies', true);

  const load = useCallback(async () => {
    if (!client?.id) return;
    try {
      const r = await axios.get(`${API}/clients/${client.id}/allergies`);
      setAllergies(r.data?.allergies || []);
      setNkda(!!r.data?.nkda);
      setLegacyText(r.data?.legacy_text || '');
      setLoaded(true);
    } catch { /* leave empty */ }
  }, [client?.id]);

  useEffect(() => { load(); }, [load]);

  const active = allergies.filter((a) => a.is_active !== false);
  const inactive = allergies.filter((a) => a.is_active === false);

  const openAdd = () => {
    setEditing(null);
    setShowForm(true);
  };
  const openEdit = (a) => {
    setEditing(a);
    setShowForm(true);
  };

  const setActive = async (a, isActive) => {
    try {
      await axios.put(`${API}/clients/${client.id}/allergies/${a.id}`, { is_active: isActive });
      toast.success(isActive ? 'Allergy reactivated' : 'Allergy marked inactive');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update allergy');
    }
  };

  const toggleNkda = async (value) => {
    try {
      await axios.post(`${API}/clients/${client.id}/allergies/nkda`, { nkda: value });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update NKDA');
    }
  };

  return (
    <div className="mb-6" data-testid="allergies-section">
      <div className="rounded-lg border bg-white shadow-sm" data-testid="allergies-display">
        <div
          className="flex items-center justify-between px-6 py-3 cursor-pointer hover:bg-gray-50 transition-colors select-none rounded-t-lg"
          onClick={toggleSection}
          data-testid="allergies-section-toggle"
        >
          <div className="flex items-center gap-2">
            {sectionOpen ? <ChevronDown className="h-4 w-4 text-gray-500" /> : <ChevronRight className="h-4 w-4 text-gray-500" />}
            <span className="text-lg font-semibold text-gray-900 flex items-center gap-1.5">
              <AlertTriangle className="h-5 w-5 text-teal-600 mr-0.5" />
              Allergies
              <span
                className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-md border ${nkda ? 'bg-green-50 text-green-700 border-green-200' : active.length > 0 ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-gray-100 text-gray-600 border-gray-300'}`}
                data-testid="allergies-counter"
              >
                {nkda ? 'NKDA' : active.length}
              </span>
            </span>
          </div>
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {active.length === 0 && (
              <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={nkda}
                  onChange={(e) => toggleNkda(e.target.checked)}
                  data-testid="nkda-checkbox"
                />
                No Known Allergies (NKDA)
              </label>
            )}
            <Button
              size="sm"
              className="bg-teal-600 hover:bg-teal-700 h-8"
              onClick={openAdd}
              data-testid="add-allergy-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> Add Allergy
            </Button>
          </div>
        </div>

        {sectionOpen && (
        <div className="px-6 pb-6">
        {nkda ? (
          <p className="text-sm text-gray-700" data-testid="nkda-display">No Known Allergies (NKDA)</p>
        ) : active.length === 0 ? (
          loaded && legacyText ? (
            <p className="text-sm text-gray-700" data-testid="legacy-allergy-text">
              {legacyText}
              <span className="block text-[11px] text-gray-500 italic mt-1">
                Free-text from intake — add structured entries above to replace it.
              </span>
            </p>
          ) : (
            <p className="text-sm italic text-gray-400">No allergies on file</p>
          )
        ) : (
          <div className="space-y-1.5">
            {active.map((a) => (
              <div key={a.id} className="p-3 border rounded-lg bg-white" data-testid={`allergy-row-${a.id}`}>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2 min-w-0">
                    <h4 className="font-semibold whitespace-nowrap">{a.allergen}</h4>
                    {a.severity && (
                      <Badge className={`flex-shrink-0 capitalize ${sevBadge(a.severity)}`}>{a.severity}</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600 flex-1 min-w-0">
                    {a.reaction && <span className="whitespace-nowrap"><strong>Reaction:</strong> {a.reaction}</span>}
                    {a.notes && <span className="truncate" title={a.notes}><strong>Notes:</strong> {a.notes}</span>}
                  </div>
                  <div className="flex space-x-2 flex-shrink-0">
                    <Button size="sm" variant="outline" onClick={() => openEdit(a)} title="Edit allergy" data-testid={`edit-allergy-${a.id}`}>
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-orange-500 hover:text-orange-700" onClick={() => setActive(a, false)} title="Mark inactive (resolved / entered in error)" data-testid={`deactivate-allergy-${a.id}`}>
                      <Archive className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {inactive.length > 0 && (
          <div className="mt-4 pt-3 border-t">
            <div
              className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded -mx-2"
              onClick={() => setShowInactive(!showInactive)}
              data-testid="toggle-inactive-allergies"
            >
              <ChevronRight className={`h-4 w-4 transition-transform ${showInactive ? 'rotate-90' : ''}`} />
              <h3 className="text-md font-semibold text-gray-500">
                Inactive Allergies ({inactive.length})
              </h3>
            </div>
            {showInactive && (
              <div className="mt-1 space-y-1">
                {inactive.map((a) => (
                  <div key={a.id} className="p-3 border rounded-lg bg-gray-50 mt-2" data-testid={`inactive-allergy-row-${a.id}`}>
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-2 min-w-0">
                        <h4 className="font-semibold text-gray-600 whitespace-nowrap">{a.allergen}</h4>
                        <Badge className="bg-gray-200 text-gray-600 flex-shrink-0">Inactive</Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500 flex-1 min-w-0">
                        {a.severity && <span className="whitespace-nowrap capitalize"><strong>Severity:</strong> {a.severity}</span>}
                        {a.reaction && <span className="whitespace-nowrap"><strong>Reaction:</strong> {a.reaction}</span>}
                        {a.notes && <span className="truncate" title={a.notes}><strong>Notes:</strong> {a.notes}</span>}
                      </div>
                      <Button size="sm" variant="outline" className="text-teal-700 flex-shrink-0" onClick={() => setActive(a, true)} data-testid={`reactivate-allergy-${a.id}`}>
                        <RotateCcw className="h-3 w-3 mr-1" /> Reactivate
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        </div>
        )}
      </div>

      <AllergyFormDialog
        open={showForm}
        onOpenChange={(o) => { setShowForm(o); if (!o) setEditing(null); }}
        clientId={client?.id}
        editing={editing}
        onSaved={load}
      />
    </div>
  );
};
